import { getSessionToken } from "../../api/auth";
import { returnToMyAccount } from "../../api/admin";
import { agentsVerdict, endExpiredLogin } from "../../Login/loginCheck";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";

/**
 * AMB-432 — forward the platform session token on every operator-API call.
 *
 * The Agents app has no central fetch client: ~15 screens call the operator API with ad-hoc
 * `fetch(`${DOCS_API_DOMAIN}/api/agents/...`)`. Rather than touch all of them (and risk
 * missing one), we install ONE scoped interceptor over the global fetch: any request to the
 * operator API (DOCS_API_DOMAIN) gets `Authorization: Bearer <session token>` injected when a
 * token exists and the caller didn't already set one. Every other request passes through
 * untouched.
 *
 * The operator derives the tenant from this token (AMB-425 validates it → AMB-428 scopes all
 * reads/writes), so `?client=` can only ever filter WITHIN the caller's own tenant — a client
 * can no longer address another tenant, and identity is no longer trusted from the request
 * body. Idempotent: safe to call on every app mount.
 */
let installed = false;

export function installAgentsAuth(): void {
  if (installed) return;
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;

  const original = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;

    if (url && url.startsWith(DOCS_API_DOMAIN)) {
      const token = getSessionToken();
      if (token) {
        const headers = new Headers(
          init?.headers ?? (input instanceof Request ? input.headers : undefined)
        );
        if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
        return original(input as RequestInfo | URL, { ...init, headers }).then((res) => {
          // LNK-7: a login the server no longer accepts ends here (log out, come back after
          // sign-in), instead of the page sitting blank. The page still gets its answer.
          if (res.status === 401)
            res
              .clone()
              .json()
              .then((body) => {
                if (agentsVerdict(res.status, body) === "expired") endExpiredLogin({ returnToMyAccount });
              })
              .catch(() => {});
          return res;
        });
      }
    }
    return original(input as RequestInfo | URL, init);
  };
}

/** Convenience client for new code (the interceptor injects the token regardless). */
export const agentsFetch = (path: string, init?: RequestInit): Promise<Response> =>
  fetch(`${DOCS_API_DOMAIN}${path}`, init);

export const agentsJson = async <T = any>(path: string, init?: RequestInit): Promise<T> => {
  const r = await agentsFetch(path, init);
  if (!r.ok) throw new Error(String(r.status));
  return (await r.json()) as T;
};
